import { ElMessage } from 'element-plus'
import { createRequest, isCancel, type AxiosResponse, type RequestConfig } from '@vea/request'
import {
  CONTENT_TYPE,
  NO_AUTH_WHITE_LIST,
  REQUEST_TIMEOUT,
  SUCCESS_CODE,
  TOKEN_PREFIX
} from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'

export interface ApiResponse<Data = unknown> {
  code: number
  data: Data
  message?: string
}

const useBrowserMock = import.meta.env.PROD && import.meta.env.VITE_USE_MOCK === 'true'

const client = createRequest({
  axiosConfig: {
    baseURL: import.meta.env.VITE_API_BASE_PATH || '/api/v1/admin',
    adapter: useBrowserMock ? 'fetch' : undefined,
    timeout: REQUEST_TIMEOUT
  },
  beforeRequest(config) {
    const userStore = useUserStoreWithOut()
    const url = config.url ?? ''
    const isAuthFree = NO_AUTH_WHITE_LIST.some((path) => url.includes(path))
    return {
      ...config,
      headers: {
        'Content-Type': CONTENT_TYPE,
        ...(userStore.token && !isAuthFree
          ? { Authorization: `${TOKEN_PREFIX}${userStore.token}` }
          : {}),
        ...config.headers
      }
    }
  },
  transformResponse(response: AxiosResponse) {
    if (response.config.responseType === 'blob') return response

    const result = response.data as ApiResponse
    if (result?.code === SUCCESS_CODE) return result.data

    if (result?.code === 401) void useUserStoreWithOut().logout()
    throw Object.assign(new Error(result?.message || `Request failed (${result?.code})`), {
      code: result?.code
    })
  },
  onError(error) {
    if (isCancel(error)) return
    const name = error instanceof Error ? error.name : undefined
    if (name === 'ApiError') return
    ElMessage.error(error instanceof Error ? error.message : String(error))
  },
  getRequestKey: (config) => config.url ?? ''
})

export default {
  get: <Data = unknown>(config: RequestConfig) => client.get<Data>(config),
  post: <Data = unknown>(config: RequestConfig) => client.post<Data>(config),
  put: <Data = unknown>(config: RequestConfig) => client.put<Data>(config),
  patch: <Data = unknown>(config: RequestConfig) =>
    client.request<Data>({ ...config, method: 'PATCH' }),
  delete: <Data = unknown>(config: RequestConfig) => client.delete<Data>(config),
  cancelRequest: (url: string | string[]) => {
    const keys = (Array.isArray(url) ? url : [url]) as readonly string[]
    client.cancelRequest(keys)
  },
  cancelAllRequest: client.cancelAllRequest
}